import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import PropertyImage from '../../components/PropertyImage';
import propertyAnalytics from '../../services/propertyAnalytics.service';
import { isTenantVisibleProperty } from '../../utils/propertyLifecycle';
import { fetchTenantVisibleListings } from '../../utils/tenantListings';
import toast from 'react-hot-toast';
import { 
  Search, 
  Filter, 
  Heart, 
  MapPin, 
  Euro, 
  Bed, 
  Home as HomeIcon,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  X,
  Square,
  Share2
} from 'lucide-react';

export default function HomeScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [allKoten, setAllKoten] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [favoriteIDs, setFavoriteIDs] = useState(new Set());
  const [searchCity, setSearchCity] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minSurface: '',
    bedrooms: '',
    availableFrom: ''
  });
  const containerRef = useRef(null);

  // Load all properties
  const loadData = useCallback(async () => {
    if (!user?.uid) return;
    setIsLoading(true);
    try {
      const tenantVisibleListings = await fetchTenantVisibleListings(db);
      
      // Load contracts to filter out active/rented properties
      // IMPORTANT: Tenants can only read their own contracts due to Firestore security rules
      // So we need to query contracts by status to find all rented properties
      // We'll try multiple approaches to get all active contracts
      let rentedPropertyIds = new Set();
      try {
        const contractsRef = collection(db, 'contracts');
        
        const activeStatuses = ['fullySigned', 'accepted', 'signedByTenant', 'signedByLandlord'];
        let allActiveContracts = [];

        // Query only contracts where current user is tenant (rule-safe).
        const tenantScopedQueries = [
          query(contractsRef, where('tenantId', '==', user.uid)),
          query(contractsRef, where('tenantID', '==', user.uid)),
          query(contractsRef, where('core.tenantId', '==', user.uid)),
          query(contractsRef, where('core.tenantID', '==', user.uid))
        ];

        for (const tenantQuery of tenantScopedQueries) {
          try {
            const snapshot = await getDocs(tenantQuery);
            const contracts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            allActiveContracts.push(...contracts);
          } catch (tenantQueryError) {
            console.warn('⚠️ Could not query tenant contracts:', tenantQueryError.message);
          }
        }

        // Remove duplicates
        const uniqueContracts = Array.from(
          new Map(allActiveContracts.map(c => [c.id, c])).values()
        );

        // Keep only active contract statuses after fetch to avoid index-heavy queries.
        const activeContracts = uniqueContracts.filter((contract) => {
          const status = contract.status || contract.core?.status;
          return activeStatuses.includes(status);
        });
        
        console.log(`✅ Total unique active contracts found: ${activeContracts.length}`);
        
        // Get property IDs that have active contracts (should be hidden)
        activeContracts.forEach(contract => {
          // Try all possible property ID fields
          const kotID = contract.core?.kotID || 
                       contract.kotID || 
                       contract.propertyId || 
                       contract.core?.propertyId ||
                       contract.property?.id ||
                       contract.propertyID ||
                       contract.core?.propertyID;
          
          if (kotID) {
            rentedPropertyIds.add(kotID);
            console.log(`📋 Contract ${contract.id} -> Property ID: ${kotID}, Status: ${contract.status || contract.core?.status}`);
          } else {
            console.warn(`⚠️ Contract ${contract.id} has no property ID field:`, contract);
          }
        });
        
        console.log('✅ Rented property IDs to filter:', Array.from(rentedPropertyIds));
        
        // If we couldn't get any contracts, try fallback: check property's isRented field
        if (rentedPropertyIds.size === 0) {
          console.log('⚠️ No contracts found via queries, will check property isRented field as fallback');
        }
      } catch (contractError) {
        console.warn('⚠️ Could not load contracts:', contractError);
        console.warn('⚠️ Will use property isRented field as fallback');
        // Continue - we'll check property.isRented field as fallback
      }
      
      // Load viewing requests to check if all slots are full.
      // If tenant cannot read this collection due to rules, continue without this enrichment.
      let viewingRequests = [];
      try {
        const viewingRequestsRef = collection(db, 'viewingRequests');
        const viewingRequestsSnapshot = await getDocs(viewingRequestsRef);
        viewingRequests = viewingRequestsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (viewingError) {
        console.warn('⚠️ Could not load viewing requests, continuing without viewingsFull calculation:', viewingError.message);
      }
      
      const koten = [];
      let filteredCount = 0;
      
      tenantVisibleListings.forEach((kot) => {
        
        // Check if property is rented via multiple methods:
        // 1. Check if property ID is in rentedPropertyIds (from contracts)
        const isRentedFromContracts = rentedPropertyIds.has(kot.id);
        
        // 2. Check if property has explicit rented flags
        // Avoid using generic availability/isActive flags here, because older
        // private listings may store those inconsistently and get hidden by mistake.
        const isRentedFromField = kot.isRented === true || kot.rented === true;
        
        // 4. Check if property has a status field indicating it's rented
        const isRentedFromStatus = kot.status === 'rented' || kot.status === 'occupied' || kot.status === 'unavailable';
        
        // 5. Check if property has isReserved field set to true (set by iOS app when contract is signed)
        const isRentedFromReserved = kot.isReserved === true;
        
        const isRented = isRentedFromContracts || isRentedFromField || isRentedFromStatus || isRentedFromReserved;
        
        // Log for debugging
        if (isRented) {
          console.log(`🚫 Filtering out rented property: ${doc.id} (${kot.titel || 'No title'})`, {
            isRentedFromContracts,
            isRentedFromField,
            isRentedFromStatus,
            isRentedFromReserved
          });
        }
        
        // Only include properties that are not rented
        if (!isRented) {
          // Check if all viewing slots are full
          const propertyViewings = viewingRequests.filter(vr => 
            (vr.kotID === kot.id || vr.propertyId === kot.id) && 
            (vr.status === 'accepted' || vr.status === 'pending')
          );
          
          // Check if property has viewingSlots configured
          const viewingSlots = kot.viewingSlots || [];
          if (viewingSlots.length > 0) {
            // For now, mark as full if there are many bookings (simplified check)
            // In a full implementation, we'd check each slot's maxBookings
            kot.viewingsFull = propertyViewings.length >= viewingSlots.length * 2; // Simplified heuristic
          } else {
            kot.viewingsFull = false;
          }
          
          koten.push(kot);
        } else {
          filteredCount++;
          // Log message is already handled above in the isRented check
        }
      });

      const toMillis = (value) => {
        const date =
          value?.toDate?.() ||
          (typeof value === 'number' ? new Date(value) : new Date(value || 0));
        const ms = date.getTime();
        return Number.isNaN(ms) ? 0 : ms;
      };
      const nowMs = Date.now();
      koten.sort((a, b) => {
        const aBoosted = a.boostActive && toMillis(a.boostExpiresAt) > nowMs ? 1 : 0;
        const bBoosted = b.boostActive && toMillis(b.boostExpiresAt) > nowMs ? 1 : 0;
        if (bBoosted !== aBoosted) return bBoosted - aBoosted;
        return toMillis(b.createdAt) - toMillis(a.createdAt);
      });
      
      console.log('✅ Total properties loaded:', koten.length);
      console.log('📊 Total properties before filter:', tenantVisibleListings.length);
      console.log(`🚫 Filtered out ${filteredCount} rented properties`);
      console.log('🚫 Rented property IDs (filtered out):', Array.from(rentedPropertyIds));
      
      if (koten.length === 0 && tenantVisibleListings.length > 0) {
        console.warn('⚠️ WARNING: All properties were filtered out! This might indicate a problem with the filtering logic.');
        const fallbackVisibleKoten = [...tenantVisibleListings];
        fallbackVisibleKoten.sort((a, b) => {
          const aBoosted = a.boostActive && toMillis(a.boostExpiresAt) > nowMs ? 1 : 0;
          const bBoosted = b.boostActive && toMillis(b.boostExpiresAt) > nowMs ? 1 : 0;
          if (bBoosted !== aBoosted) return bBoosted - aBoosted;
          return toMillis(b.createdAt) - toMillis(a.createdAt);
        });

        if (fallbackVisibleKoten.length > 0) {
          console.warn(`⚠️ Applying fallback visibility set: ${fallbackVisibleKoten.length} properties`);
          setAllKoten(fallbackVisibleKoten);
          setFiltered(fallbackVisibleKoten);
          return;
        }
      }
      
      setAllKoten(koten);
      setFiltered(koten);
    } catch (error) {
      console.error('Error loading properties:', error);
      toast.error('Fout bij het laden van panden: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Load user favorites
  const loadFavorites = useCallback(async () => {
    if (!user) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const favorites = userData.favorites || [];
        setFavoriteIDs(new Set(favorites));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }, [user]);

  // Toggle favorite
  const toggleFavorite = useCallback(async (propertyId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    const newFavorites = new Set(favoriteIDs);
    if (newFavorites.has(propertyId)) {
      newFavorites.delete(propertyId);
    } else {
      newFavorites.add(propertyId);
    }

    setFavoriteIDs(newFavorites);

    try {
      await setDoc(doc(db, 'users', user.uid), {
        favorites: Array.from(newFavorites)
      }, { merge: true });
    } catch (error) {
      console.error('Error updating favorites:', error);
      setFavoriteIDs(favoriteIDs);
    }
  }, [user, favoriteIDs, navigate]);


  // Effects
  useEffect(() => {
    if (allKoten.length === 0) {
      loadData();
    }
    loadFavorites();
  }, [loadData, loadFavorites, allKoten.length]);

  useEffect(() => {
    applyFilters();
  }, [allKoten, searchCity, filters]);

  // Apply filters
  const applyFilters = () => {
    let filteredKoten = [...allKoten];

    // City filter
    if (searchCity.trim()) {
      const cityLower = searchCity.toLowerCase();
      filteredKoten = filteredKoten.filter(kot => 
        kot.stad?.toLowerCase().includes(cityLower) ||
        kot.adres?.toLowerCase().includes(cityLower)
      );
    }

    // Price filters
    if (filters.minPrice) {
      filteredKoten = filteredKoten.filter(kot => 
        kot.prijs && kot.prijs >= parseInt(filters.minPrice)
      );
    }
    if (filters.maxPrice) {
      filteredKoten = filteredKoten.filter(kot => 
        kot.prijs && kot.prijs <= parseInt(filters.maxPrice)
      );
    }

    // Surface filter
    if (filters.minSurface) {
      filteredKoten = filteredKoten.filter(kot => 
        kot.oppervlakte && kot.oppervlakte >= parseInt(filters.minSurface)
      );
    }

    // Bedrooms filter
    if (filters.bedrooms) {
      filteredKoten = filteredKoten.filter(kot => 
        (kot.slaapkamers || kot.kamers) && parseInt(kot.slaapkamers || kot.kamers) >= parseInt(filters.bedrooms)
      );
    }

    if (filters.availableFrom) {
      const selectedDate = new Date(filters.availableFrom);
      selectedDate.setHours(0, 0, 0, 0);
      filteredKoten = filteredKoten.filter(kot => {
        if (!kot.beschikbaarVanaf) return false;
        const availableDate = kot.beschikbaarVanaf?.toDate
          ? kot.beschikbaarVanaf.toDate()
          : new Date(kot.beschikbaarVanaf);
        if (Number.isNaN(availableDate.getTime())) return false;
        availableDate.setHours(0, 0, 0, 0);
        return availableDate <= selectedDate;
      });
    }

    setFiltered(filteredKoten);
  };

  const clearFilters = () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      minSurface: '',
      bedrooms: '',
      availableFrom: ''
    });
    setSearchCity('');
  };

  return (
    <div 
      ref={containerRef}
      className="min-h-screen bg-[var(--market-bg)]"
    >
      {/* Hero Section with Search */}
      <div className="relative overflow-hidden bg-[linear-gradient(140deg,#4F9DE0_0%,#5EABEB_42%,#6C8FD8_100%)]">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -right-28 -top-28 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.2)_0%,rgba(255,255,255,0.07)_45%,transparent_75%)]" />
          <div 
            className="absolute -top-40 -right-40 w-96 h-96 bg-white rounded-full opacity-[0.08] animate-float-slow"
            style={{ 
              animationDuration: '25s',
              animationDelay: '0s'
            }}
          ></div>
          <div 
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-white rounded-full opacity-[0.07] animate-float-slow"
            style={{ 
              animationDuration: '30s',
              animationDelay: '3s'
            }}
          ></div>
          <div className="absolute inset-0 opacity-[0.035] [background-image:radial-gradient(#ffffff_0.7px,transparent_0.7px)] [background-size:6px_6px]" />
        </div>

        <div className="relative z-10 container-professional py-16 md:py-20">
          <div className="max-w-4xl mx-auto">
            {/* Welcome Message */}
            <div className="text-center mb-10">
              <h1 className="text-5xl md:text-6xl font-semibold tracking-tight text-white mb-4">
                Welkom terug
              </h1>
              <p className="text-xl text-white/90 mb-2 font-medium">
                Vind je ideale studentenkamer
              </p>
              <p className="text-lg text-white/75">
                Ontdek de beste kamers in België en Nederland, gefilterd op jouw voorkeuren
              </p>
            </div>

            {/* Enhanced Search Bar */}
            <div className="bg-white/97 rounded-[24px] shadow-[0_20px_40px_rgba(15,23,42,0.14)] border border-white/35 p-6 md:p-8 backdrop-blur-sm">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Zoek op stad of adres..."
                      value={searchCity}
                      onChange={(e) => setSearchCity(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] text-lg transition-all duration-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]"
                      data-tutorial="search-bar"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`group flex items-center justify-center space-x-2 px-6 py-4 font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/35 ${
                      showFilters 
                        ? 'bg-[#5EABEB] text-white hover:bg-[#4A98D6] shadow-sm' 
                        : 'bg-white border border-[#5EABEB]/25 hover:bg-[#5EABEB]/10 text-slate-700'
                    }`}
                    data-tutorial="filter-button"
                  >
                    <SlidersHorizontal className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
                    <span>Filters</span>
                    {(filters.minPrice || filters.maxPrice || filters.minSurface || filters.bedrooms || filters.availableFrom) && (
                      <span className="ml-1 bg-white text-ios-blue rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                        {(filters.minPrice || filters.maxPrice ? 1 : 0) + (filters.minSurface ? 1 : 0) + (filters.bedrooms ? 1 : 0) + (filters.availableFrom ? 1 : 0)}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => applyFilters()}
                    className="group flex items-center justify-center space-x-2 px-8 py-4 bg-[#5EABEB] hover:bg-[#4A98D6] text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-[#5EABEB]/35"
                  >
                    <Search className="w-5 h-5 group-hover:scale-105 transition-transform duration-200" />
                    <span>Zoeken</span>
                  </button>
                </div>
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-6 pt-6 border-t border-gray-200 animate-in slide-in-from-top-5 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Min Price */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Min. Prijs (€/maand)
                      </label>
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.minPrice}
                          onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all"
                        />
                      </div>
                    </div>

                    {/* Max Price */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Max. Prijs (€/maand)
                      </label>
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.maxPrice}
                          onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all"
                        />
                      </div>
                    </div>

                    {/* Min Surface */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Min. Oppervlakte (m²)
                      </label>
                      <div className="relative">
                        <Square className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="number"
                          placeholder="Min m²"
                          value={filters.minSurface}
                          onChange={(e) => setFilters({ ...filters, minSurface: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all"
                        />
                      </div>
                    </div>

                    {/* Bedrooms */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Min. Slaapkamers
                      </label>
                      <div className="relative">
                        <Bed className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <select
                          value={filters.bedrooms}
                          onChange={(e) => setFilters({ ...filters, bedrooms: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all bg-white"
                        >
                          <option value="">Alle</option>
                          <option value="1">1+</option>
                          <option value="2">2+</option>
                          <option value="3">3+</option>
                          <option value="4">4+</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Beschikbaarheid vanaf
                      </label>
                      <input
                        type="date"
                        value={filters.availableFrom}
                        onChange={(e) => setFilters({ ...filters, availableFrom: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#5EABEB]/35 focus:border-[#5EABEB] transition-all bg-white"
                      />
                    </div>
                  </div>

                  {/* Filter Actions */}
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={clearFilters}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                    >
                      <X className="w-4 h-4" />
                      <span>Filters wissen</span>
                    </button>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="px-6 py-2 bg-[#5EABEB] hover:bg-[#4A98D6] text-white font-semibold rounded-lg transition-all duration-200"
                    >
                      Filters toepassen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-b from-transparent to-[var(--market-bg)]" />
      </div>

      {/* Content Section */}
      <div className="container-professional py-12 relative">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Beschikbare Kamers
            </h2>
            <p className="text-gray-600">
              {filtered.length > 0 ? (
                <span className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5 text-[#5EABEB]" />
                  <span>{filtered.length} kamers beschikbaar</span>
                </span>
              ) : (
                'Ontdek de beste studentenkamers in België'
              )}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-ios-blue mx-auto mb-4"></div>
                <Sparkles className="w-8 h-8 text-ios-blue absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" />
              </div>
              <div className="text-gray-600 font-medium mt-4">Kamers laden…</div>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-32 h-32 bg-gradient-to-br from-[#EAF4FE] to-[#F3F9FF] rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-[#5EABEB]/20">
              <HomeIcon className="w-16 h-16 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Geen kamers gevonden</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Probeer andere zoektermen of filters om meer resultaten te zien
            </p>
            <button
              onClick={() => {
                setSearchCity('');
                setShowFilters(false);
              }}
              className="px-8 py-4 bg-[#5EABEB] hover:bg-[#4A98D6] text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-[1px]"
            >
              Reset filters
            </button>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((kot) => (
              <div 
                key={kot.id} 
                className="group bg-[var(--surface)] rounded-[18px] shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-hover)] border border-[var(--surface-border)] hover:border-[#5EABEB]/35 overflow-hidden transition-all duration-200 hover:-translate-y-1 cursor-pointer"
                onClick={() => {
                  propertyAnalytics.trackPropertyClick(kot.id);
                  navigate(`/property/${kot.id}`);
                }}
                data-tutorial="property-card"
                data-property-id={kot.id}
              >
                <div className="relative overflow-hidden">
                  {/* Viewings Full Overlay */}
                  {kot.viewingsFull && (
                    <div className="absolute inset-0 z-10 pointer-events-none rounded-2xl overflow-hidden">
                      {/* Very light blur overlay */}
                      <div className="absolute inset-0 bg-gray-500/15 backdrop-blur-[1px]"></div>
                      {/* Text badge */}
                      <div className="absolute inset-0 flex items-end justify-center pb-5">
                        <span className="text-white text-base font-bold bg-gray-600/90 px-4 py-2 rounded-xl shadow-lg">
                          Bezichtigingen vol
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Property Image */}
                  <div className="relative h-56 overflow-hidden">
                    <PropertyImage 
                      property={kot}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"
                      width={400}
                      height={300}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>

                  {/* Share + Favorite Buttons */}
                  <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(kot.id);
                      }}
                      className="p-3 bg-white/92 backdrop-blur-sm rounded-full border border-slate-200 hover:bg-white transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                      data-tutorial="favorite-button"
                      aria-label="Favoriet toggelen"
                    >
                      <Heart 
                        className={`w-5 h-5 transition-all duration-300 ${
                          favoriteIDs.has(kot.id) 
                            ? 'text-red-500 fill-current scale-105' 
                            : 'text-slate-400 hover:text-red-400'
                        }`} 
                      />
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}/p/${kot.id}`;
                        const title = kot.titel || 'Bekijk dit pand op StudyStay';
                        if (navigator.share) {
                          try { await navigator.share({ title, text: `${title} in ${kot.stad || ''} - €${kot.prijs || 0}/maand`, url }); } catch {}
                        } else {
                          try { await navigator.clipboard.writeText(url); toast.success('Link gekopieerd!'); } catch { toast.error('Kopiëren mislukt'); }
                        }
                      }}
                      className="p-3 bg-white/92 backdrop-blur-sm rounded-full border border-slate-200 hover:bg-white transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md"
                      aria-label="Pand delen"
                    >
                      <Share2 className="w-5 h-5 text-slate-400 hover:text-[#5EABEB]" />
                    </button>
                  </div>

                  {kot.beschikbaarVanaf && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 text-white rounded-lg text-xs font-semibold shadow-sm z-10">
                      Beschikbaar vanaf {new Date(kot.beschikbaarVanaf?.toDate ? kot.beschikbaarVanaf.toDate() : kot.beschikbaarVanaf).toLocaleDateString('nl-BE')}
                    </div>
                  )}

                  {/* Price Badge */}
                  {kot.prijs && (
                    <div className="absolute bottom-4 left-4 px-4 py-2 bg-[#5EABEB] text-white rounded-full font-semibold shadow-sm border border-white/20">
                      €{kot.prijs}/maand
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#2B6FA6] transition-colors line-clamp-2">
                      {kot.titel || kot.adres || 'Naamloos pand'}
                    </h3>
                    <div className="flex items-center text-gray-600 text-sm">
                      <MapPin className="w-4 h-4 mr-1 text-[#5EABEB]" />
                      <span>{kot.stad || 'Stad niet opgegeven'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4 pt-4 border-t border-gray-100">
                    {kot.oppervlakte && (
                      <div className="flex items-center text-gray-600">
                        <Bed className="w-5 h-5 mr-2 text-gray-400" />
                        <span className="font-medium">{kot.oppervlakte}m²</span>
                      </div>
                    )}
                    <div className="flex items-center text-[#2B6FA6] font-semibold">
                      <span>Bekijk details</span>
                      <svg className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
      
      {/* Tutorial is now global in WebsiteLayout - removed from here */}
    </div>
  );
}
